import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../lib/auth';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Lock, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const isForced = user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error('SalesPRO: La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error('SalesPRO: Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      const resp = await api.changePassword({
        current_password: isForced ? null : form.current_password,
        new_password: form.new_password
      });
      // 🛡️ El backend ahora devuelve un nuevo access_token con el token_version bumped
      // (invalida sesiones anteriores). Si viene, lo guardamos antes de llamar a /auth/me.
      if (resp?.data?.access_token) {
        authService.setToken(resp.data.access_token);
      }
      // Refrescar user info
      const me = await api.getMe();
      const stored = authService.getUser();
      authService.setUser({ ...stored, ...me.data });
      toast.success('SalesPRO: Contraseña actualizada con éxito');
      // Forzar full reload a la ruta correcta según rol
      const target = me.data?.role_id === 'cliente' ? '/client-portal' : '/dashboard';
      window.location.assign(target);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al cambiar contraseña');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8F9FA] to-[#E2E8F0] p-6">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <ShieldCheck size={32} className="text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#022C22]">
              {isForced ? 'Configura tu nueva contraseña' : 'Cambiar contraseña'}
            </h1>
            <p className="text-sm text-[#64748B] mt-2">
              {isForced
                ? 'SalesPRO requiere que cambies tu contraseña en el primer acceso'
                : 'Define una nueva contraseña segura'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isForced && (
              <div>
                <Label>Contraseña actual</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    data-testid="current-password-input"
                    value={form.current_password}
                    onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                    required
                    className="h-11 pr-10"
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}
            <div>
              <Label>Nueva contraseña</Label>
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  data-testid="new-password-input"
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  required
                  minLength={6}
                  className="h-11 pr-10"
                  placeholder="Mínimo 6 caracteres"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]" data-testid="toggle-show-password">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmar nueva contraseña</Label>
              <Input
                type={showPwd ? 'text' : 'password'}
                data-testid="confirm-password-input"
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="submit-change-password"
              className="w-full h-11 font-semibold"
              disabled={loading}
            >
              <Lock size={16} className="mr-2" />
              {loading ? 'Guardando...' : 'Actualizar contraseña'}
            </Button>
          </form>

          <p className="text-xs text-center text-[#94A3B8] mt-6">
            SalesPRO — Sistema profesional de gestión de ventas
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
